import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * Detects `?2fa_challenge=<id>` in URL (set by OAuth callback after primary auth)
 * and prompts for the TOTP code. On success, exchanges challenge → session cookie.
 * Also surfaces `?oauth_error=<reason>` and `?2fa_error=<reason>` as toasts.
 */
export default function TwoFactorChallengeHandler() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [challengeId, setChallengeId] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const cid = params.get("2fa_challenge");
    const oauthErr = params.get("oauth_error");
    if (oauthErr) {
      toast.error(`Sign-in failed: ${oauthErr}`);
      params.delete("oauth_error");
      nav({ pathname: loc.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
    }
    if (cid) {
      setChallengeId(cid);
      setCode("");
    }
  }, [loc.search, loc.pathname, nav]);

  const close = () => {
    setChallengeId(null);
    const params = new URLSearchParams(loc.search);
    params.delete("2fa_challenge");
    nav({ pathname: loc.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
  };

  const complete = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter 6-digit authenticator code"); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/2fa/complete", { challenge_id: challengeId, code }, { withCredentials: true });
      await refresh();
      toast.success(`Welcome ${data.name || data.email || "back"}`);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "2FA verification failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={!!challengeId} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="oauth-2fa-dialog" className="max-w-sm bg-[color:var(--bg-surface)] border-[color:var(--border-default)] text-[color:var(--text-primary)]">
        <DialogHeader>
          <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">◈ 2FA REQUIRED</div>
          <DialogTitle className="font-serif-elite text-2xl">Authenticator code</DialogTitle>
          <DialogDescription className="text-[color:var(--text-secondary)]">
            Enter the 6-digit code from your authenticator app to finish signing in.
          </DialogDescription>
        </DialogHeader>
        <input
          data-testid="oauth-2fa-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => e.key === "Enter" && complete()}
          placeholder="6-digit TOTP"
          autoFocus
          className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2.5 text-lg font-mono-data tracking-widest focus:outline-none focus:border-[color:var(--border-focus)]"
        />
        <button
          data-testid="oauth-2fa-verify-btn"
          onClick={complete}
          disabled={verifying}
          className="w-full mt-1 bg-white text-black py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors"
        >{verifying ? "VERIFYING…" : "COMPLETE SIGN-IN →"}</button>
      </DialogContent>
    </Dialog>
  );
}
